import React, { useState, useEffect, useCallback } from "react";
import {
  FiActivity,
  FiFilter,
  FiCalendar,
  FiUser,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiDownload,
  FiCopy,
  FiLock,
  FiUnlock,
  FiShare2,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiCheck,
  FiAlertCircle
} from "react-icons/fi";
import {
  getAuditTrail,
  CredentialAccessLog,
  AuditTrailFilters,
  CredentialAccessAction
} from "../../utils/api/endpoints/vault";

interface VaultAuditTrailProps {
  isOpen: boolean;
  onClose: () => void;
}

const ACTION_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  view: { label: "Viewed", icon: <FiEye className="w-4 h-4" />, color: "text-blue-600 bg-blue-50" },
  decrypt: { label: "Decrypted", icon: <FiUnlock className="w-4 h-4" />, color: "text-purple-600 bg-purple-50" },
  create: { label: "Created", icon: <FiPlus className="w-4 h-4" />, color: "text-green-600 bg-green-50" },
  update: { label: "Updated", icon: <FiEdit2 className="w-4 h-4" />, color: "text-yellow-600 bg-yellow-50" },
  delete: { label: "Deleted", icon: <FiTrash2 className="w-4 h-4" />, color: "text-red-600 bg-red-50" },
  export: { label: "Exported", icon: <FiDownload className="w-4 h-4" />, color: "text-indigo-600 bg-indigo-50" },
  share: { label: "Shared", icon: <FiShare2 className="w-4 h-4" />, color: "text-teal-600 bg-teal-50" },
  copy: { label: "Copied", icon: <FiCopy className="w-4 h-4" />, color: "text-gray-600 bg-gray-50" },
  unlock: { label: "Unlocked Vault", icon: <FiUnlock className="w-4 h-4" />, color: "text-green-600 bg-green-50" },
  lock: { label: "Locked Vault", icon: <FiLock className="w-4 h-4" />, color: "text-gray-600 bg-gray-50" },
  master_password_reset: { label: "Master Password Hard Reset", icon: <FiAlertCircle className="w-4 h-4" />, color: "text-red-700 bg-red-100 font-bold" },
  master_password_change: { label: "Master Password Rotated", icon: <FiLock className="w-4 h-4" />, color: "text-emerald-700 bg-emerald-100 font-bold" }
};

const VaultAuditTrail: React.FC<VaultAuditTrailProps> = ({ isOpen, onClose }) => {
  const [logs, setLogs] = useState<CredentialAccessLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pageSize: 20, totalPages: 0 });
  const [summary, setSummary] = useState<Array<{ action: string; count: number }>>([]);
  const [filters, setFilters] = useState<AuditTrailFilters>({ page: 1, pageSize: 20 });
  const [showFilters, setShowFilters] = useState(false);

  const fetchAuditTrail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAuditTrail(filters);
      setLogs(response.logs);
      setPagination(response.pagination);
      setSummary(response.summary.actionCounts);
    } catch (error) {
      console.error("Error fetching audit trail:", error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (isOpen) fetchAuditTrail();
  }, [isOpen, fetchAuditTrail]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const getActionConfig = (action: string) => {
    return ACTION_CONFIG[action] || { label: action, icon: <FiActivity className="w-4 h-4" />, color: "text-gray-600 bg-gray-50" };
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-3xl bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <FiActivity className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Audit Trail</h2>
              <p className="text-sm text-gray-500">{pagination.total} total events</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg transition-colors ${showFilters ? "bg-indigo-100 text-indigo-600" : "hover:bg-gray-100 text-gray-600"}`}
            >
              <FiFilter className="w-5 h-5" />
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <FiX className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex gap-3 overflow-x-auto">
          {summary.map((item) => {
            const config = getActionConfig(item.action);
            return (
              <button
                key={item.action}
                onClick={() => setFilters(f => ({ ...f, action: f.action === item.action ? undefined : item.action as CredentialAccessAction, page: 1 }))}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  filters.action === item.action ? "ring-2 ring-indigo-500 " + config.color : config.color + " opacity-70 hover:opacity-100"
                }`}
              >
                {config.icon}
                <span>{config.label}</span>
                <span className="bg-white/50 px-1.5 rounded">{item.count}</span>
              </button>
            );
          })}
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="px-6 py-4 bg-white border-b border-gray-200 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.startDate || ""}
                onChange={(e) => setFilters(f => ({ ...f, startDate: e.target.value || undefined, page: 1 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={filters.endDate || ""}
                onChange={(e) => setFilters(f => ({ ...f, endDate: e.target.value || undefined, page: 1 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            {(filters.startDate || filters.endDate || filters.action) && (
              <button
                onClick={() => setFilters({ page: 1, pageSize: 20 })}
                className="col-span-2 text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              >
                <FiX className="w-4 h-4" /> Clear all filters
              </button>
            )}
          </div>
        )}

        {/* Logs List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <FiActivity className="w-12 h-12 mb-3 opacity-30" />
              <p>No audit logs found</p>
              {(filters.action || filters.startDate || filters.endDate) && (
                <button onClick={() => setFilters({ page: 1, pageSize: 20 })} className="mt-2 text-indigo-600 hover:underline text-sm">
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {logs.map((log) => {
                const config = getActionConfig(log.action);
                return (
                  <div key={log.log_id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-4">
                      {/* Action Icon */}
                      <div className={`p-2 rounded-lg ${config.color}`}>
                        {config.icon}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-900">
                            {log.user?.username || `User #${log.user_id}`}
                          </span>
                          <span className="text-gray-500">{config.label.toLowerCase()}</span>
                          {log.entry && (
                            <span className="text-gray-900 font-medium truncate">
                              "{log.entry.name}"
                            </span>
                          )}
                          {log.vault && !log.entry && (
                            <span className="text-gray-900 font-medium truncate">
                              vault "{log.vault.name}"
                            </span>
                          )}
                          {!log.success && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              <FiAlertCircle className="w-3 h-3" /> Failed
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <FiCalendar className="w-3.5 h-3.5" />
                            {formatDate(log.created_at)}
                          </span>
                          {log.entry?.credential_type && (
                            <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                              {log.entry.credential_type}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Success indicator */}
                      <div className={`flex-shrink-0 ${log.success ? "text-green-500" : "text-red-500"}`}>
                        {log.success ? <FiCheck className="w-5 h-5" /> : <FiAlertCircle className="w-5 h-5" />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Showing {((pagination.page - 1) * pagination.pageSize) + 1} to {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setFilters(f => ({ ...f, page: (f.page || 1) - 1 }))}
                disabled={pagination.page <= 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm font-medium">
                {pagination.page} / {pagination.totalPages}
              </span>
              <button
                onClick={() => setFilters(f => ({ ...f, page: (f.page || 1) + 1 }))}
                disabled={pagination.page >= pagination.totalPages}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VaultAuditTrail;

